import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Star, Tags, Pencil, Share2, Copy, RefreshCw, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Flat fields shown in the "Other" tab (existing schema kept for backwards compat)
const OTHER_FIELDS = [
  { key: "labor_rate", label: "Labor Rate ($/h)" },
  { key: "scan_pre", label: "Pre-Scan" },
  { key: "scan_post", label: "Post-Scan" },
  { key: "front_camera_cal", label: "Front Camera Cal" },
  { key: "front_radar_cal", label: "Front Radar Cal" },
  { key: "rear_radar_cal", label: "Rear Radar Cal" },
  { key: "surround_view_cal", label: "Surround View Cal" },
  { key: "blind_spot_cal", label: "Blind Spot Cal" },
  { key: "park_assist_cal", label: "Park Assist Cal" },
  { key: "sas_reset", label: "SAS Reset" },
  { key: "headlamp_aim", label: "Headlamp Aim" },
  { key: "road_test", label: "Road Test" },
];

const CAL_TYPES = [
  { key: "static",  label: "Static",  color: "text-cyan-300",    dot: "bg-cyan-400" },
  { key: "dynamic", label: "Dynamic", color: "text-fuchsia-300", dot: "bg-fuchsia-400" },
  { key: "dual",    label: "Dual",    color: "text-amber-300",   dot: "bg-amber-400" },
];

const DEFAULT = {
  name: "Default 2026", is_default: true,
  labor_rate: 95, scan_pre: 75, scan_post: 75,
  front_camera_cal: 450, front_radar_cal: 425, rear_radar_cal: 395,
  surround_view_cal: 525, blind_spot_cal: 295, park_assist_cal: 195,
  sas_reset: 85, headlamp_aim: 175, road_test: 95, notes: "",
  adas_operations: {},
};

export default function MasterRateTables() {
  const [rows, setRows] = useState([]);
  const [catalog, setCatalog] = useState({ operations: [], defaults: {} });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT);
  const [tab, setTab] = useState("static");
  const [shareOpen, setShareOpen] = useState(null);

  const load = () => api.get("/master/rate-tables").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/master/rate-tables/adas-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const buildPublicUrl = (t) => `${window.location.origin}/pricing/${t}`;

  const copyLink = async (t) => {
    try {
      await navigator.clipboard.writeText(buildPublicUrl(t));
      toast.success("Link copied");
    } catch (e) { toast.error("Copy failed"); }
  };

  const rotateToken = async (id) => {
    try {
      const r = await api.post(`/master/rate-tables/${id}/rotate-share-token`);
      toast.success("New link generated — old one is now invalid");
      setShareOpen((s) => s && s.id === id ? { ...s, share_token: r.data.share_token, share_enabled: true } : s);
      load();
    } catch (e) { toast.error("Rotate failed"); }
  };

  const toggleShare = async (id, enabled) => {
    try {
      await api.patch(`/master/rate-tables/${id}/toggle-share?enabled=${enabled}`);
      toast.success(enabled ? "Link enabled" : "Link disabled");
      setShareOpen((s) => s && s.id === id ? { ...s, share_enabled: enabled } : s);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      // Ensure ADAS matrix is always populated when creating so backend defaults kick in
      const payload = { ...form };
      if (editingId) {
        await api.patch(`/master/rate-tables/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/master/rate-tables", payload);
        toast.success("Created");
      }
      setOpen(false); setEditingId(null); setForm(DEFAULT); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const makeDefault = async (id) => {
    await api.patch(`/master/rate-tables/${id}`, { is_default: true });
    toast.success("Default updated");
    load();
  };

  const edit = (r) => {
    // Ensure adas_operations exists locally so inputs are controlled
    const ao = { ...(catalog.defaults || {}), ...(r.adas_operations || {}) };
    setForm({ ...r, adas_operations: ao });
    setEditingId(r.id);
    setOpen(true);
    setTab("static");
  };

  const startNew = () => {
    setForm({ ...DEFAULT, adas_operations: { ...(catalog.defaults || {}) } });
    setEditingId(null);
    setOpen(true);
    setTab("static");
  };

  const setAdasPrice = (opKey, typeKey, value) => {
    const v = parseFloat(value);
    setForm((f) => ({
      ...f,
      adas_operations: {
        ...(f.adas_operations || {}),
        [opKey]: {
          ...((f.adas_operations || {})[opKey] || {}),
          [typeKey]: Number.isFinite(v) ? v : 0,
        },
      },
    }));
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="rate-tables-page">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center">
            <Tags className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Master Data</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Rate Tables</h1>
            <p className="text-sm text-slate-400 mt-1">Insurance-approved ADAS calibration fees by type (Static / Dynamic / Dual).</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(DEFAULT); } }}>
          <DialogTrigger asChild><Button data-testid="rate-new-btn" onClick={startNew} className="bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4 mr-2" />New Rate Table</Button></DialogTrigger>
          <DialogContent className="bg-[#0B1120] border-slate-800 max-w-4xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Edit Rate Table" : "New Rate Table"}</DialogTitle></DialogHeader>            <div className="space-y-4 mt-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-400">Name</Label>
                  <Input data-testid="rate-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#030712] border-slate-800" /></div>
                <label className="flex items-end gap-2 text-sm text-slate-300 pb-2">
                  <input type="checkbox" data-testid="rate-is-default" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
                  Use as company default
                </label>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-1 border-b border-slate-800">
                {CAL_TYPES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`rate-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold transition ${tab === t.key ? `${t.color} border-b-2 border-current` : "text-slate-500 hover:text-slate-300"}`}
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${t.dot}`} />{t.label}
                  </button>
                ))}
                <button
                  data-testid="rate-tab-other"
                  onClick={() => setTab("other")}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.16em] font-bold transition ${tab === "other" ? "text-slate-100 border-b-2 border-slate-300" : "text-slate-500 hover:text-slate-300"}`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-2 bg-slate-400" />Other
                </button>
              </div>

              {/* ADAS matrix tabs */}
              {tab !== "other" && (
                <div className="space-y-1.5" data-testid={`rate-panel-${tab}`}>
                  <div className="grid grid-cols-[1fr_140px] items-center px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    <div>Operation</div>
                    <div className="text-right">Price ($)</div>
                  </div>
                  {(catalog.operations || []).map((op) => (
                    <div key={op.key} className="grid grid-cols-[1fr_140px] items-center px-3 py-1.5 rounded-md hover:bg-slate-900/40">
                      <div className="text-sm text-slate-200">{op.label}</div>
                      <Input
                        data-testid={`rate-adas-${op.key}-${tab}`}
                        type="number"
                        step="0.01"
                        className="bg-[#030712] border-slate-800 font-mono text-right"
                        value={((form.adas_operations || {})[op.key] || {})[tab] ?? ""}
                        onChange={(e) => setAdasPrice(op.key, tab, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Other tab: legacy flat fields */}
              {tab === "other" && (
                <div className="grid grid-cols-3 gap-3" data-testid="rate-panel-other">
                  {OTHER_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{f.label}</Label>
                      <Input data-testid={`rate-field-${f.key}`} type="number" step="0.01" className="mt-1 bg-[#030712] border-slate-800 font-mono" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: parseFloat(e.target.value) || 0 })} />
                    </div>
                  ))}
                </div>
              )}

              <Button data-testid="rate-save-btn" onClick={submit} className="bg-blue-600 hover:bg-blue-500 w-full">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {rows.length === 0 && <div className="col-span-2 text-center text-slate-500 py-10 rounded-xl border border-slate-800 bg-[#0B1120]">No rate tables yet. Click &quot;New Rate Table&quot; to create one.</div>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`rate-card-${r.id}`} className={`rounded-xl border ${r.is_default ? "border-amber-500/40 bg-amber-500/5" : "border-slate-800 bg-[#0B1120]"} p-5`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-lg font-medium">{r.name}</div>
                {r.is_default && <div className="text-[10px] uppercase tracking-wide font-bold text-amber-400 mt-1 flex items-center gap-1"><Star className="w-3 h-3" />Default</div>}
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`rate-share-${r.id}`} onClick={() => setShareOpen(r)} className="text-slate-400 hover:text-emerald-400 p-1" title="Share public pricing link"><Share2 className="w-4 h-4" /></button>
                <button data-testid={`rate-edit-${r.id}`} onClick={() => edit(r)} className="text-slate-400 hover:text-blue-400 p-1"><Pencil className="w-4 h-4" /></button>
              </div>
            </div>

            {/* ADAS matrix */}
            {r.adas_operations && (catalog.operations || []).length > 0 && (
              <div className="mb-3 rounded-md border border-slate-800/60 overflow-hidden">
                <div className="grid grid-cols-[1fr_repeat(3,minmax(0,60px))] bg-slate-900/50 px-3 py-1.5 text-[9px] uppercase tracking-wider font-bold text-slate-500">
                  <div>ADAS Op</div>
                  {CAL_TYPES.map((t) => (
                    <div key={t.key} className={`text-right ${t.color}`}>{t.label.slice(0,3)}</div>
                  ))}
                </div>
                {(catalog.operations || []).map((op) => {
                  const prices = r.adas_operations[op.key] || {};
                  return (
                    <div key={op.key} className="grid grid-cols-[1fr_repeat(3,minmax(0,60px))] px-3 py-1 text-[11px] border-t border-slate-800/40">
                      <div className="text-slate-300 truncate" title={op.label}>{op.label}</div>
                      {CAL_TYPES.map((t) => (
                        <div key={t.key} className="text-right font-mono text-slate-400">${(prices[t.key] ?? 0).toFixed(2)}</div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Legacy flat fields (collapsed summary) */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
              {OTHER_FIELDS.slice(0,4).map((f) => (
                <div key={f.key} className="flex justify-between border-b border-slate-800/40 py-0.5">
                  <span className="text-slate-500">{f.label}</span>
                  <span className="font-mono text-blue-300/80">${r[f.key]}</span>
                </div>
              ))}
            </div>

            {!r.is_default && (
              <Button data-testid={`rate-make-default-${r.id}`} variant="outline" size="sm" onClick={() => makeDefault(r.id)} className="mt-3 w-full border-slate-700 bg-slate-900/40">
                <Star className="w-3 h-3 mr-1" /> Make Default
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Share dialog */}
      <Dialog open={!!shareOpen} onOpenChange={(o) => !o && setShareOpen(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-4 h-4 text-emerald-400" /> Share pricing link
            </DialogTitle>
          </DialogHeader>
          {shareOpen && (
            <div className="space-y-4 mt-2" data-testid="share-dialog">
              <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Table</div>
                <div className="text-sm font-medium">{shareOpen.name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-2">Public URL — send to sales reps or partners</div>
                <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-md p-2 font-mono text-xs break-all">
                  <span className="text-blue-300 flex-1 min-w-0" data-testid="share-public-url">{buildPublicUrl(shareOpen.share_token)}</span>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button onClick={() => copyLink(shareOpen.share_token)} className="bg-emerald-600 hover:bg-emerald-500 flex-1" data-testid="share-copy-btn"><Copy className="w-3.5 h-3.5 mr-2" /> Copy link</Button>
                  <Button variant="outline" onClick={() => window.open(buildPublicUrl(shareOpen.share_token), "_blank")} className="border-slate-700" data-testid="share-preview-btn"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
                </div>
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-slate-200">Link status</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Disable to instantly revoke access.</div>
                  </div>
                  <Button size="sm" variant="outline" className={shareOpen.share_enabled === false ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"} onClick={() => toggleShare(shareOpen.id, !(shareOpen.share_enabled !== false))} data-testid="share-toggle-btn">
                    {shareOpen.share_enabled === false ? <><EyeOff className="w-3.5 h-3.5 mr-1.5" /> Disabled</> : <><Eye className="w-3.5 h-3.5 mr-1.5" /> Enabled</>}
                  </Button>
                </div>
              </div>
              <div className="border-t border-slate-800 pt-3">
                <Button variant="outline" onClick={() => rotateToken(shareOpen.id)} className="w-full border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/15" data-testid="share-rotate-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Generate a new link (invalidates the old one)
                </Button>
                <div className="text-[10px] text-slate-600 mt-2 italic text-center">Use this if a link has leaked. Anyone using the old URL will see &quot;expired&quot;.</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
